import classNames from 'classnames';
import React, { useRef, useState } from 'react';
import { useMutateCard } from '../../../hooks/apolloHooks/useMutateCard';
import { DisableCardIcon } from '../../svgs/ExpressonSVGs';
import Button from '../Button';
import RightsWrapper from '../RightsWrapper';

type CardDisabledEditorProps = {
	cardId: string | number
	cardType: string
	maskedPan: string
	cardName?: string
	isCreditCard?: boolean
	isCompany?: boolean
	onDisableSuccess?: () => void
}

const CardDisabledEditor: React.FC<CardDisabledEditorProps> = ({
	cardId,
	cardType,
	maskedPan,
	cardName,
	isCreditCard,
	isCompany,
	onDisableSuccess
}) => {
	const [isConfirming, setIsConfirming] = useState(false)
	const [disabledError, setDisabledError] = useState<string | null>(null)
	const dialogRef = useRef<HTMLDialogElement>(null)
	const { disableCard, loading, error } = useMutateCard()

	const handleDisableClick = () => {
		setDisabledError(null)
		setIsConfirming(true)
		dialogRef.current?.showModal()
	}

	const handleConfirm = async () => {
		try {
			setDisabledError(null)
			const result = await disableCard({
				cardId: typeof cardId === 'string' ? parseInt(cardId) : cardId,
				reason: 'By user'
			})

			if (result.data) {
				setIsConfirming(false)
				dialogRef.current?.close()
				// Call the success callback to close card editing state
				if (onDisableSuccess) {
					onDisableSuccess()
				}
			}
		} catch (err) {
			console.error('Failed to disable card:', err)
			setDisabledError('Kunde inte spärra kortet. Försök igen.')
		}
	}

	const handleCancel = () => {
		setIsConfirming(false)
		setDisabledError(null)
		dialogRef.current?.close()
	}

	// Map short names to full names in Swedish
	const cardTypeLabel = {
		'P': 'Smartkort',
		'K': isCompany ? 'Företagskort' : 'Tankkort',
		'B': 'Bankkort',
		'S': 'Spärrat'
	}[cardType] || cardType

	return (
		<div className="card-disabled-editor">
			<RightsWrapper
				disableButtonText={'Spärra kort'}
				disableButtonClasses="disable-fill-color body-text"
				disableButtonIcon={<DisableCardIcon width={24} height={24} />}
			>
				<button
					type="button"
					className={classNames(
						'card-disabled-editor__button button-disable',
						'button-edit',
						'body-text',
						{ 'invert': !isCreditCard }
					)}
					onClick={handleDisableClick}
					disabled={loading}
				>
					<DisableCardIcon width={24} height={24} />
					Spärra kort
				</button>
			</RightsWrapper>


			<dialog
				ref={dialogRef}
				className="card-disabled-editor__dialog"
				onCancel={handleCancel}
			>
				<div className="card-disabled-editor__dialog-content">
					<h2 className="card-disabled-editor__title">Bekräfta spärrning av kort</h2>

					<div className="card-disabled-editor__details">
						{cardName && cardName.trim().length > 0 && (
							<div className="card-disabled-editor__detail-row">
								<span className="card-disabled-editor__label">Kortnamn:</span>
								<span className="card-disabled-editor__value">{cardName}</span>
							</div>
						)}
						<div className="card-disabled-editor__detail-row">
							<span className="card-disabled-editor__label">Korttyp:</span>
							<span className="card-disabled-editor__value">{cardTypeLabel}</span>
						</div>
						<div className="card-disabled-editor__detail-row">
							<span className="card-disabled-editor__label">Kortnummer:</span>
							<span className="card-disabled-editor__value">{maskedPan}</span>
						</div>
					</div>

					<div className="card-disabled-editor__info">
						<p>
							Spärrning av kort kommer att slå igenom direkt och kortet kommer inte längre gå att använda.
						</p>
					</div>

					{disabledError && (
						<p className="card-disabled-editor__error">
							{disabledError}
						</p>
					)}
					{error && (
						<p className="card-disabled-editor__error">
							Kunde inte spärra kortet. Försök igen eller kontakta kundservice.
						</p>
					)}

					<div className="card-disabled-editor__actions">
						<Button
							className="body-text"
							type="button"
							onClick={handleConfirm}
							disabled={loading}
						>
							{loading ? 'Spärrar...' : 'Spärra kort'}
						</Button>
						<Button
							type="button"
							onClick={handleCancel}
							className="button-cancel body-text"
							disabled={loading}
						>
							Avbryt
						</Button>
					</div>
				</div>
			</dialog>
		</div>
	)
}

export default CardDisabledEditor
